import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    Bug,
    Lightbulb,
    HelpCircle,
    MoreHorizontal,
    Send,
    Loader2,
    CheckCircle2,
    Clock,
    AlertCircle,
    XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { value: 'bug', label: 'Bug Report', icon: Bug, description: 'Something isn\'t working correctly' },
    { value: 'feature', label: 'Feature Request', icon: Lightbulb, description: 'Suggest a new feature' },
    { value: 'question', label: 'Question', icon: HelpCircle, description: 'Need help with something' },
    { value: 'other', label: 'Other', icon: MoreHorizontal, description: 'General feedback' },
];

const STATUS_CONFIG = {
    open: { label: 'Open', icon: AlertCircle, color: 'bg-amber-500' },
    in_progress: { label: 'In Progress', icon: Clock, color: 'bg-blue-500' },
    resolved: { label: 'Resolved', icon: CheckCircle2, color: 'bg-emerald-500' },
    closed: { label: 'Closed', icon: XCircle, color: 'bg-gray-500' },
};

export default function ReportIssue() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [myIssues, setMyIssues] = useState([]);
    
    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('bug');

    const fetchMyIssues = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/issues`);
            setMyIssues(res.data);
        } catch (error) {
            console.error('Failed to fetch issues:', error);
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchMyIssues().finally(() => setLoading(false));
    }, [fetchMyIssues]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!title.trim()) {
            toast.error('Please enter a title');
            return;
        }
        if (!description.trim()) {
            toast.error('Please describe the issue');
            return;
        }
        
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/issues`, {
                title: title.trim(),
                description: description.trim(),
                category,
                page_url: window.location.href
            });
            
            toast.success('Issue reported successfully! We\'ll look into it.');
            setTitle('');
            setDescription('');
            setCategory('bug');
            fetchMyIssues();
        } catch (error) {
            toast.error('Failed to submit issue');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return format(new Date(dateStr), 'MMM d, yyyy');
    };

    const selectedCategory = CATEGORIES.find(c => c.value === category);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                    <Bug className="w-8 h-8 text-rose-500" />
                    Report an Issue
                </h1>
                <p className="text-muted-foreground mt-1">Help us improve by reporting bugs or suggesting features</p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Report Form */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Submit a Report</CardTitle>
                        <CardDescription>We read every report and appreciate your feedback</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Category Selection */}
                            <div className="space-y-2">
                                <Label>Category</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    {CATEGORIES.map((cat) => {
                                        const Icon = cat.icon;
                                        const isSelected = category === cat.value;
                                        return (
                                            <button
                                                key={cat.value}
                                                type="button"
                                                onClick={() => setCategory(cat.value)}
                                                className={`p-3 rounded-lg border text-left transition-all ${
                                                    isSelected 
                                                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                                                        : 'border-border hover:border-primary/50'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Icon className={`w-4 h-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                                                    <span className="font-medium text-sm">{cat.label}</span>
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">{cat.description}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Title */}
                            <div className="space-y-2">
                                <Label htmlFor="title">Title</Label>
                                <Input
                                    id="title"
                                    placeholder="Brief summary of the issue"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    maxLength={100}
                                />
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    placeholder="Please describe the issue in detail. Include steps to reproduce if reporting a bug."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={5}
                                    maxLength={2000}
                                />
                                <p className="text-xs text-muted-foreground text-right">
                                    {description.length}/2000
                                </p>
                            </div>

                            {/* Submit Button */}
                            <Button type="submit" disabled={submitting} className="w-full gap-2">
                                {submitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                                {submitting ? 'Submitting...' : 'Submit Report'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* My Issues */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">My Reports</CardTitle>
                        <CardDescription>Track the status of your submitted reports</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : myIssues.length === 0 ? (
                            <div className="text-center py-8">
                                <Bug className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                                <p className="text-muted-foreground">No reports submitted yet</p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                                {myIssues.map((issue) => {
                                    const statusConfig = STATUS_CONFIG[issue.status] || STATUS_CONFIG.open;
                                    const StatusIcon = statusConfig.icon;
                                    const catConfig = CATEGORIES.find(c => c.value === issue.category);
                                    const CatIcon = catConfig?.icon || Bug;
                                    
                                    return (
                                        <div
                                            key={issue.id}
                                            className="p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-start gap-2 min-w-0">
                                                    <CatIcon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-sm truncate">{issue.title}</p>
                                                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                                            {issue.description}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Badge className={`${statusConfig.color} text-white text-xs flex-shrink-0`}>
                                                    <StatusIcon className="w-3 h-3 mr-1" />
                                                    {statusConfig.label}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center justify-between mt-2 pt-2 border-t">
                                                <span className="text-xs text-muted-foreground">
                                                    {formatDate(issue.created_at)}
                                                </span>
                                                {issue.admin_notes && (
                                                    <span className="text-xs text-primary">
                                                        Response received
                                                    </span>
                                                )}
                                            </div>
                                            {issue.admin_notes && (
                                                <div className="mt-2 p-2 rounded bg-primary/5 border border-primary/20">
                                                    <p className="text-xs font-medium text-primary">Admin Response:</p>
                                                    <p className="text-xs text-muted-foreground mt-1">{issue.admin_notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
